import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { filter, switchMap, take } from 'rxjs/operators';
import {
  EnterpriseSearchService,
  FlightOriginalResultsFetcher,
  ServiceType,
  UserService
} from '@sabstravtech/obtservices/angular';
import { FlightEnterpriseSearchInterface } from '@sabstravtech/obtservices/base';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

import { BaseComponent } from '../../../core/components/base_components/base-componet';
import { ThemeService } from '../../../core/services/theme.service';

@Component({
  selector: 'app-flights',
  templateUrl: './flights.component.html',
  styleUrls: ['./flights.component.css'],
  standalone: false
})
export class FlightsComponent extends BaseComponent implements OnInit {
  flightsViewController: any;
  srchFromResults = false;
  searchParams: FlightEnterpriseSearchInterface;
  /** True while cached AI flight results for a results uuid are loading. */
  loadingOriginalResults = false;

  constructor(
    public searchService: EnterpriseSearchService,
    private titleService: Title,
    translateService: TranslateService,
    public themeService: ThemeService,
    private route: ActivatedRoute,
    private flightOriginalResultsFetcher: FlightOriginalResultsFetcher,
    private userService: UserService
  ) {
    super(titleService, translateService);
  }

  /**
   * The search tabs read originalUserSearch once on init and only draw result cards
   * when it has both airports, so they wait for any search or cached load to finish.
   */
  get showSearchTabs(): boolean {
    return (
      !this.searchService.search_loading &&
      !this.searchParams?.isLoadingSSO &&
      !this.loadingOriginalResults
    );
  }

  ngOnInit(): void {
    this.titleService.setTitle(
      `Flight results ${this.themeService.isLightningEnv ? ' - LightUk' : ''}`
    );
    this.searchParams = this.searchService.searches[ServiceType.Flight];

    // The AI flight agent links to /results/flights?results=uuid. Subscribing rather
    // than reading the snapshot means a second link pasted into the same tab loads too.
    // In a freshly opened tab the search objects only exist once the user's services
    // have loaded, so the link is read after that.
    const queryParamsOnceSearchesExist = this.userService.fullUserDetails.userServices.pipe(
      filter((services) => !!services && !!this.searchService.searches[ServiceType.Flight]),
      take(1),
      switchMap(() => {
        this.searchParams = this.searchService.searches[ServiceType.Flight];
        return this.route.queryParamMap;
      })
    );
    this.subscribe(queryParamsOnceSearchesExist, (params: ParamMap) => {
      const resultsId = params.get('results');
      if (resultsId) {
        this.loadOriginalResults(resultsId);
      } else {
        // A normal search after an expired link must not keep showing the expired message
        this.searchParams.originalResultsNotFound.next(false);
      }
    });
  }

  private loadOriginalResults(resultsId: string): void {
    this.loadingOriginalResults = true;
    this.searchParams
      .loadOriginalResults(resultsId, this.flightOriginalResultsFetcher)
      .finally(() => {
        this.loadingOriginalResults = false;
      });
  }
}
